import BarChart from "../BarChart";
import { colours } from "../../../../StyleConstants";
import { Virtuoso } from "react-virtuoso";
import React, { useCallback, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import HistogramChart from "../HistogramChart";

interface Props {
    chartData: any;
    dataDictionary: any;
    alignAxis: boolean;
    setViewField?: any;
    field?: string;
    showAsTable: boolean;
    showIssueOptions?: boolean;
}

interface Dashlet {
    key: string;
    isNumeric: boolean;
    data: any[];
    missingData: any[];
    title: string;
    subtitle?: string;
}

const CHART_COLUMNS = 3;
const INCREASE_VIEWPORT_BY = 600;

const VariableDashletGrid = (props: Props) => {
    const itemsPerRow = props.showAsTable ? 1 : CHART_COLUMNS;

    const dashlets = useMemo<Dashlet[]>(() => {
        return Object.keys(props.chartData).map((key) => {
            const dictionaryKey = props.field ?? key;
            const isNumeric = ['integer', 'double'].includes(props.dataDictionary?.[dictionaryKey]?.typeData)
                && props.chartData[key].data.length > 5;

            let data: any[] = [];
            const missingData: any[] = [];
            if (isNumeric) {
                props.chartData[key].data.forEach((row: any) => {
                    if (!isNaN(row.label) && row.label !== '') {
                        data.push({
                            ...row,
                            label: parseFloat(row.label),
                        })
                    } else {
                        missingData.push(row);
                    }
                })
            } else {
                data = props.chartData[key].data;
            }

            return {
                key,
                isNumeric,
                data,
                missingData,
                title: props.field ? key : props.dataDictionary?.[dictionaryKey]?.label,
                subtitle: props.field ? undefined : key,
            };
        });
    }, [props.chartData, props.dataDictionary, props.field]);

    const rows = useMemo<Dashlet[][]>(() => {
        const chunked: Dashlet[][] = [];
        for (let index = 0; index < dashlets.length; index += itemsPerRow) {
            chunked.push(dashlets.slice(index, index + itemsPerRow));
        }
        return chunked;
    }, [dashlets, itemsPerRow]);

    const partialOptions = useMemo(() => !props.alignAxis ? {} : {
        sync: {
            axes: 'y' as const,
            nodeInteraction: false
        }
    }, [props.alignAxis]);

    const itemContent = useCallback((_index: number, row: Dashlet[]) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {row.map((dashlet) => (
                <Grid
                    item
                    xs={12}
                    md={12 / itemsPerRow}
                    key={dashlet.key}
                    sx={{ display: 'flex', flexDirection: 'column', '& > *': { flexGrow: 1 } }}
                >
                    {dashlet.isNumeric ? (
                        <HistogramChart
                            data={dashlet.data}
                            missingData={dashlet.missingData}
                            title={dashlet.title}
                            subtitle={dashlet.subtitle}
                            ddAvailable={false}
                            imagingData={undefined}
                            breakdownAvailable={props.setViewField !== undefined}
                            breakdownClicked={props.setViewField}
                            partialOptions={partialOptions}
                            showAsTable={props.showAsTable}
                            showIssueOptions={props.showIssueOptions}
                        />
                    ) : (
                        <BarChart
                            data={dashlet.data}
                            title={dashlet.title}
                            subtitle={dashlet.subtitle}
                            ddAvailable={false}
                            imagingData={undefined}
                            breakdownAvailable={props.setViewField !== undefined}
                            breakdownClicked={props.setViewField}
                            getBarFill={(data: any, defaultFill: any) => {
                                return data.isShadowValue ? colours.error.error : defaultFill;
                            }}
                            partialOptions={partialOptions}
                            showAsTable={props.showAsTable}
                            showIssueOptions={props.showIssueOptions}
                        />
                    )}
                </Grid>
            ))}
        </Box>
    ), [itemsPerRow, partialOptions, props.setViewField, props.showAsTable, props.showIssueOptions]);

    return (
        <Virtuoso
            key={props.showAsTable ? 'table' : 'chart'}
            style={{ height: '100%', width: '100%' }}
            data={rows}
            computeItemKey={(index, row) => row[0]?.key ?? index}
            itemContent={itemContent}
            increaseViewportBy={INCREASE_VIEWPORT_BY}
        />
    )
}

export default VariableDashletGrid;
